import { useContext, useState } from 'react';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import { UserContext } from '@lib/contexts/UserProvider';
import { Button, Props as ButtonProps } from '@components/ButtonV2';
import {
  useMyPaymentMethodsQuery,
  useStartTrialPlanSubscriptionMutation,
} from '@gql/generated';
import { snackbarVar } from '@lib/apollo/policies';
import { Body, Footer, Header, Modal } from '@organisms/modals/base';
import { Box, Flex } from '@components/layout/Grid';
import { P } from '@components/typography/Paragraph';
import Spinner from '@components/Spinner';

const ConfirmStartSubscriptionModal = ({ onDelete, onClose }) => {
  const { data, loading } = useMyPaymentMethodsQuery();
  const [currentUser] = useContext(UserContext);

  const lastFour =
    data?.myPaymentMethods?.find(item => item.isDefault)?.lastFour ||
    data?.myPaymentMethods[0]?.lastFour;

  const amount = currentUser?.nextBillingCycle?.amount;

  return (
    <Modal open={true} handleClose={onClose}>
      {loading ? (
        <Body>
          <Flex justifyContent="center" py={60}>
            <Spinner size={24} spinnerColor="#242424" />
          </Flex>
        </Body>
      ) : (
        <>
          <Header className="md:absolute w-full" handleClose={onClose} />
          <Body>
            <Box mb={0} mt={20}>
              <P fontSize={'24px'} color="#242424" bold>
                <Trans id="settings_subscriptions.activate_subscription_modal.title" />
              </P>
              <P fontSize="16px" color="#242424">
                <Trans
                  id="settings_subscriptions.activate_subscription_modal.desc"
                  values={{
                    amount,
                    lastDigits: lastFour,
                  }}
                />
              </P>
            </Box>
          </Body>
          <Footer>
            <Flex width={1} justifyContent="space-between">
              <Button outline onClick={onClose}>
                <Trans id="settings_subscriptions.activate_subscription_modal.cancel" />
              </Button>
              <Button onClick={onDelete}>
                <Trans id="settings_subscriptions.activate_subscription_modal.activate" />
              </Button>
            </Flex>
          </Footer>
        </>
      )}
    </Modal>
  );
};

type Props = ButtonProps & { children: React.ReactChild; onComplete?(): void };

export const StartTrialSubscriptionButton = ({
  children,
  ...otherProps
}: Props) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [_, { refetch }] = useContext(UserContext);
  const [
    startTrialPlanSubscription,
    { loading },
  ] = useStartTrialPlanSubscriptionMutation({
    onCompleted(resp) {
      if (resp?.startTrialPlanSubscription?.success) {
        // show snackbar
        snackbarVar({
          isSnackBarOpen: true,
          snackBarMessage: t`trial_2023.activate_trial_subscription.success_message`,
        });
        refetch({ onlyBaseProperties: true });
      } else {
        // show snackbar
        snackbarVar({
          isSnackBarOpen: true,
          snackBarMessage: t`trial_2023.activate_trial_subscription.failed_message`,
        });
      }
    },
  });

  const confirmStartingSubscription = () => {
    setShowConfirmModal(true);
  };

  return (
    <>
      <Button
        loading={loading}
        {...otherProps}
        onClick={confirmStartingSubscription}
      >
        {children}
      </Button>
      {showConfirmModal ? (
        <ConfirmStartSubscriptionModal
          onClose={() => setShowConfirmModal(false)}
          onDelete={startTrialPlanSubscription}
        />
      ) : null}
    </>
  );
};
